// Een score delen via een link: ?share=<gegevens>. Alles zit in de link zelf, er is geen server.
// De gegevens zijn een korte lijst getallen (base64url), nooit tekst van een speler: een link kan dus niets
// anders bevatten dan een score. parseShare controleert elk veld; wat niet klopt wordt afgewezen.
import { RANKS } from '../game/economy';

export const SHARE_PARAM = 'share';
const VERSION = 1;
/** Langer dan dit is geen geldige link; zo hoeven we nooit iets groots te decoderen. */
const MAX_LENGTH = 120;

export interface ShareScore {
  /** Verdiend in de shift, in hele centen */
  cents: number;
  deliveries: number;
  /** Dagsterren (0 tot 3) */
  dayStars: number;
  bestCombo: number;
  /** Rang van de speler (1 tot en met het aantal rangen) */
  rank: number;
  /** Was het de uitdaging van de dag? Dan hoort er een datum (JJJJ-MM-DD) bij. */
  daily: boolean;
  date: string;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const isInt = (v: unknown, min: number, max: number): v is number => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;

function validDate(s: unknown): s is string {
  if (typeof s !== 'string' || !DATE.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(s);
}

const toBase64Url = (text: string) => btoa(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const fromBase64Url = (text: string) => atob(text.replace(/-/g, '+').replace(/_/g, '/'));

/** Maakt de waarde voor ?share= van een score. Een ongeldige score wordt bijgesneden naar iets geldigs. */
export function encodeShare(score: ShareScore): string {
  const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, Math.round(Number.isFinite(v) ? v : 0)));
  const daily = score.daily && validDate(score.date);
  const list = [
    VERSION,
    clamp(score.cents, 0, 1_000_000),
    clamp(score.deliveries, 0, 99),
    clamp(score.dayStars, 0, 3),
    clamp(score.bestCombo, 0, 99),
    clamp(score.rank, 1, RANKS.length),
    daily ? 1 : 0,
    daily ? score.date : '',
  ];
  return toBase64Url(JSON.stringify(list));
}

/** Leest de waarde van ?share=. Geeft null bij alles wat niet precies een geldige score is. */
export function parseShare(raw: string | null | undefined): ShareScore | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > MAX_LENGTH || !/^[A-Za-z0-9_-]+$/.test(raw)) return null;
  let list: unknown;
  try {
    list = JSON.parse(fromBase64Url(raw));
  } catch {
    return null;
  }
  if (!Array.isArray(list) || list.length !== 8) return null;
  const [version, cents, deliveries, dayStars, bestCombo, rank, daily, date] = list;
  if (version !== VERSION) return null;
  if (!isInt(cents, 0, 1_000_000) || !isInt(deliveries, 0, 99) || !isInt(dayStars, 0, 3) || !isInt(bestCombo, 0, 99) || !isInt(rank, 1, RANKS.length)) return null;
  if (daily !== 0 && daily !== 1) return null;
  if (daily === 1 ? !validDate(date) : date !== '') return null;
  return { cents, deliveries, dayStars, bestCombo, rank, daily: daily === 1, date: date as string };
}

/** De volledige link, bijvoorbeeld voor het klembord. `base` is de pagina zonder zoekdeel (origin + pad). */
export const shareUrl = (base: string, score: ShareScore) => `${base}?${SHARE_PARAM}=${encodeShare(score)}`;
